import type {
    BranchComparison,
    BranchComparisonItem,
    PopularBrand,
    PopularBrands,
    RevenueDynamics,
    RevenueDynamicsBucket,
    RevenueDynamicsSummary,
    RevenueSummary,
    RevenueSummaryItem,
    SalesTransaction,
    SalesTransactionDiscount,
    SalesTransactionPayment,
    SalesTransactionProduct,
    SalesTransactionRefund,
    SalesTransactionRefundProduct,
    SalesTransactionsPage,
    TopCategories,
    TopCategory,
    TopProduct,
    TopProducts,
} from '@/api/models';
import { tetriFromDecimal } from '@/utils/money';

/**
 * Wire → domain for sales money (BOS-171, completing BOS-162).
 *
 * The sales endpoints send decimal GEL, and the Sales dashboard was the one
 * area BOS-162 did not reach — so a revenue tile rendered `GEL 455.00` through
 * `Intl`'s currency style while the Purchases table beside it rendered
 * `₾ 455.00` through `formatGel`. Two notations for one currency, on one
 * product, on adjacent screens.
 *
 * Every amount gains a `*Tetri` twin here, at the one edge that crosses, so the
 * dashboards format and compare integers. The decimal fields stay as the wire's
 * echo; nothing downstream should compute on them.
 *
 * `tetriFromDecimal` parses digit positions rather than multiplying by 100, so
 * a revenue figure cannot drift by a tetri between the tile and the ledger.
 */

/* -------------------------------------------------------------------------- */
/* Transactions                                                                */
/* -------------------------------------------------------------------------- */

const toDiscount = (
    wire: SalesTransactionDiscount | null,
): SalesTransactionDiscount | null =>
    wire === null
        ? null
        : {
              ...wire,
              amountTetri: tetriFromDecimal(wire.amount),
              // `value` is money only on a fixed discount. On a percentage one
              // it is a percentage, and converting it would turn 10% into
              // 1,000 tetri — the most convincing wrong number on the receipt.
              valueTetri:
                  wire.type === 'fixed' ? tetriFromDecimal(wire.value) : null,
          };

const toProduct = (wire: SalesTransactionProduct): SalesTransactionProduct => ({
    ...wire,
    priceTetri: tetriFromDecimal(wire.price),
    listTotalTetri: tetriFromDecimal(wire.listTotal),
    saleDiscountShareTetri: tetriFromDecimal(wire.saleDiscountShare),
    totalTetri: tetriFromDecimal(wire.total),
    discount: toDiscount(wire.discount),
});

const toPayment = (wire: SalesTransactionPayment): SalesTransactionPayment => ({
    ...wire,
    amountTetri: tetriFromDecimal(wire.amount),
});

const toRefundProduct = (
    wire: SalesTransactionRefundProduct,
): SalesTransactionRefundProduct => ({
    ...wire,
    priceTetri: tetriFromDecimal(wire.price),
    totalTetri: tetriFromDecimal(wire.total),
});

const toRefund = (
    wire: SalesTransactionRefund | null,
): SalesTransactionRefund | null =>
    wire === null
        ? null
        : { ...wire, products: wire.products.map(toRefundProduct) };

export const toSalesTransaction = (
    wire: SalesTransaction,
): SalesTransaction => ({
    ...wire,
    subtotalTetri: tetriFromDecimal(wire.subtotal),
    lineDiscountTotalTetri: tetriFromDecimal(wire.lineDiscountTotal),
    discountTotalTetri: tetriFromDecimal(wire.discountTotal),
    totalTetri: tetriFromDecimal(wire.total),
    feesTetri: tetriFromDecimal(wire.fees),
    cart: wire.cart.map(toProduct),
    cartDiscount: toDiscount(wire.cartDiscount),
    payments: wire.payments.map(toPayment),
    refund: toRefund(wire.refund),
});

export const toSalesTransactionsPage = (
    wire: SalesTransactionsPage,
): SalesTransactionsPage => ({
    ...wire,
    items: wire.items.map(toSalesTransaction),
});

/* -------------------------------------------------------------------------- */
/* Dashboard widgets                                                           */
/* -------------------------------------------------------------------------- */

const toRevenueSummaryItem = (
    wire: RevenueSummaryItem,
): RevenueSummaryItem => ({
    ...wire,
    amountTetri: tetriFromDecimal(wire.amount),
});

export const toRevenueSummary = (wire: RevenueSummary): RevenueSummary => ({
    ...wire,
    items: wire.items.map(toRevenueSummaryItem),
});

const toRevenueDynamicsBucket = (
    wire: RevenueDynamicsBucket,
): RevenueDynamicsBucket => ({
    ...wire,
    revenueTetri: tetriFromDecimal(wire.revenue),
    markupTetri: tetriFromDecimal(wire.markup),
    averageReceiptTetri: tetriFromDecimal(wire.averageReceipt),
});

const toRevenueDynamicsSummary = (
    wire: RevenueDynamicsSummary,
): RevenueDynamicsSummary => ({
    ...wire,
    totalRevenueTetri: tetriFromDecimal(wire.totalRevenue),
    totalDiscountsTetri: tetriFromDecimal(wire.totalDiscounts),
    totalMarkupTetri: tetriFromDecimal(wire.totalMarkup),
    averageReceiptTetri: tetriFromDecimal(wire.averageReceipt),
});

export const toRevenueDynamics = (wire: RevenueDynamics): RevenueDynamics => ({
    ...wire,
    buckets: wire.buckets.map(toRevenueDynamicsBucket),
    summary: toRevenueDynamicsSummary(wire.summary),
});

const toTopProduct = (wire: TopProduct): TopProduct => ({
    ...wire,
    revenueTetri: tetriFromDecimal(wire.revenue),
});

export const toTopProducts = (wire: TopProducts): TopProducts => ({
    ...wire,
    items: wire.items.map(toTopProduct),
});

const toPopularBrand = (wire: PopularBrand): PopularBrand => ({
    ...wire,
    revenueTetri: tetriFromDecimal(wire.revenue),
});

export const toPopularBrands = (wire: PopularBrands): PopularBrands => ({
    ...wire,
    items: wire.items.map(toPopularBrand),
});

const toTopCategory = (wire: TopCategory): TopCategory => ({
    ...wire,
    totalRevenueTetri: tetriFromDecimal(wire.totalRevenue),
});

export const toTopCategories = (wire: TopCategories): TopCategories => ({
    ...wire,
    totalRevenueTetri: tetriFromDecimal(wire.totalRevenue),
    categories: wire.categories.map(toTopCategory),
});

const toBranchComparisonItem = (
    wire: BranchComparisonItem,
): BranchComparisonItem => ({
    ...wire,
    totalRevenueTetri: tetriFromDecimal(wire.totalRevenue),
});

export const toBranchComparison = (
    wire: BranchComparison,
): BranchComparison => ({
    ...wire,
    totalRevenueTetri: tetriFromDecimal(wire.totalRevenue),
    branches: wire.branches.map(toBranchComparisonItem),
});
